import { FC } from "react";
import { useTranslation } from "react-i18next";
import { Label } from "flowbite-react";
import {
  IRecurrenceRule,
  RecurrenceFrequency,
} from "../../types/order/IOrderTemplate";
import { LGBDatepicker } from "../LgbDatepicker/LgbDatepicker";
import { Timestamp } from "../../types";
import { fieldFullWidth } from "@design-system";

export interface RecurrenceFieldsProps {
  value: IRecurrenceRule;
  onChange: (patch: Partial<IRecurrenceRule>) => void;
}

/** Weekday indices used across the app: 0 = Monday ... 6 = Sunday. */
const WEEKDAYS = [0, 1, 2, 3, 4, 5, 6];
const WEEKDAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

/**
 * Recurrence rule editor (frequency, interval, weekly weekdays / monthly day,
 * start date, end condition, lead time). Controlled: the parent owns the rule
 * and receives immutable patches via `onChange`.
 */
export const RecurrenceFields: FC<RecurrenceFieldsProps> = ({
  value,
  onChange,
}) => {
  const { t } = useTranslation(["orders", "common"]);

  const handleToggleWeekday = (day: number) => {
    const selected = new Set(value.byWeekday ?? []);
    if (selected.has(day)) {
      selected.delete(day);
    } else {
      selected.add(day);
    }
    onChange({ byWeekday: Array.from(selected).sort((a, b) => a - b) });
  };

  const intervalUnitKey =
    value.frequency === "daily"
      ? "orders:templates.recurrence.unit_days"
      : value.frequency === "weekly"
        ? "orders:templates.recurrence.unit_weeks"
        : "orders:templates.recurrence.unit_months";

  return (
    <div className="space-y-4">
      {/* Frequency */}
      <div>
        <div className="mb-2 block">
          <Label
            htmlFor="recurrence-frequency"
            value={t("orders:templates.recurrence.frequency_label")}
          />
        </div>
        <select
          id="recurrence-frequency"
          className={fieldFullWidth}
          value={value.frequency}
          onChange={(e) =>
            onChange({ frequency: e.target.value as RecurrenceFrequency })
          }
        >
          <option value="daily">
            {t("orders:templates.recurrence.freq_daily")}
          </option>
          <option value="weekly">
            {t("orders:templates.recurrence.freq_weekly")}
          </option>
          <option value="monthly">
            {t("orders:templates.recurrence.freq_monthly")}
          </option>
        </select>
      </div>

      {/* Interval */}
      <div>
        <div className="mb-2 block">
          <Label
            htmlFor="recurrence-interval"
            value={t("orders:templates.recurrence.interval_label")}
          />
        </div>
        <div className="flex items-center gap-2">
          <input
            id="recurrence-interval"
            type="number"
            min={1}
            className={`${fieldFullWidth} max-w-24`}
            value={value.interval}
            onChange={(e) =>
              onChange({
                interval: Math.max(1, parseInt(e.target.value, 10) || 1),
              })
            }
          />
          <span className="text-sm text-secondary">{t(intervalUnitKey)}</span>
        </div>
      </div>

      {/* Weekly: weekday picker */}
      {value.frequency === "weekly" && (
        <div>
          <div className="mb-2 block">
            <Label value={t("orders:templates.recurrence.weekdays_label")} />
          </div>
          <div className="flex flex-wrap gap-2">
            {WEEKDAYS.map((day) => {
              const active = (value.byWeekday ?? []).includes(day);
              return (
                <button
                  key={day}
                  type="button"
                  aria-pressed={active}
                  aria-label={t(
                    `orders:templates.recurrence.weekday_short.${WEEKDAY_KEYS[day]}`,
                  )}
                  onClick={() => handleToggleWeekday(day)}
                  className={
                    "w-11 h-9 rounded-lg text-sm font-medium border transition-colors " +
                    (active
                      ? "bg-brand text-white border-brand"
                      : "bg-surface text-text border-border hover:bg-surface-muted")
                  }
                >
                  {t(
                    `orders:templates.recurrence.weekday_short.${WEEKDAY_KEYS[day]}`,
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Monthly: day of month */}
      {value.frequency === "monthly" && (
        <div>
          <div className="mb-2 block">
            <Label
              htmlFor="recurrence-monthday"
              value={t("orders:templates.recurrence.monthday_label")}
            />
          </div>
          <input
            id="recurrence-monthday"
            type="number"
            min={1}
            max={31}
            className={`${fieldFullWidth} max-w-24`}
            value={value.byMonthDay ?? 1}
            onChange={(e) => {
              const raw = parseInt(e.target.value, 10) || 1;
              onChange({ byMonthDay: Math.min(31, Math.max(1, raw)) });
            }}
          />
        </div>
      )}

      {/* Start date */}
      <div>
        <div className="mb-2 block">
          <Label
            htmlFor="recurrence-start"
            value={t("orders:templates.recurrence.start_label")}
          />
        </div>
        <LGBDatepicker
          inputId="recurrence-start"
          selectedDate={
            value.startDate
              ? Timestamp.toMoment(value.startDate).toDate()
              : undefined
          }
          allowPastDates
          maxDate={null}
          overlayCalendar
          select={(date) =>
            onChange({ startDate: Timestamp.toFormattedDate(date) })
          }
        />
      </div>

      {/* End condition */}
      <div>
        <div className="mb-2 block">
          <Label
            htmlFor="recurrence-end-mode"
            value={t("orders:templates.recurrence.end_label")}
          />
        </div>
        <select
          id="recurrence-end-mode"
          className={fieldFullWidth}
          value={value.endMode}
          onChange={(e) =>
            onChange({
              endMode: e.target.value as IRecurrenceRule["endMode"],
            })
          }
        >
          <option value="never">
            {t("orders:templates.recurrence.end_never")}
          </option>
          <option value="onDate">
            {t("orders:templates.recurrence.end_on_date")}
          </option>
          <option value="afterCount">
            {t("orders:templates.recurrence.end_after_count")}
          </option>
        </select>

        {value.endMode === "onDate" && (
          <div className="mt-2">
            <Label
              htmlFor="recurrence-end-date"
              value={t("orders:templates.recurrence.end_date_label")}
            />
            <LGBDatepicker
              inputId="recurrence-end-date"
              selectedDate={
                value.endDate
                  ? Timestamp.toMoment(value.endDate).toDate()
                  : undefined
              }
              minDate={
                value.startDate
                  ? Timestamp.toMoment(value.startDate).toDate()
                  : undefined
              }
              allowPastDates
              maxDate={null}
              overlayCalendar
              select={(date) =>
                onChange({ endDate: Timestamp.toFormattedDate(date) })
              }
            />
          </div>
        )}

        {value.endMode === "afterCount" && (
          <input
            type="number"
            min={1}
            aria-label={t("orders:templates.recurrence.count_label")}
            placeholder={t("orders:templates.recurrence.count_label")}
            className={`${fieldFullWidth} mt-2 max-w-32`}
            value={value.count ?? 1}
            onChange={(e) =>
              onChange({
                count: Math.max(1, parseInt(e.target.value, 10) || 1),
              })
            }
          />
        )}
      </div>

      {/* Lead time */}
      <div>
        <div className="mb-2 block">
          <Label
            htmlFor="recurrence-lead-time"
            value={t("orders:templates.recurrence.lead_time_label")}
          />
        </div>
        <input
          id="recurrence-lead-time"
          type="number"
          min={0}
          max={90}
          className={`${fieldFullWidth} max-w-24`}
          value={value.leadTimeDays}
          onChange={(e) =>
            onChange({
              leadTimeDays: Math.min(
                90,
                Math.max(0, parseInt(e.target.value, 10) || 0),
              ),
            })
          }
        />
        <p className="mt-1 text-xs text-secondary">
          {t("orders:templates.recurrence.lead_time_hint")}
        </p>
      </div>
    </div>
  );
};

export default RecurrenceFields;
